import { useState, useRef, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../new-components/ui/button';
import { SecuritiesDisclaimer } from './SecuritiesDisclaimer';
import { InlineError } from './InlineError';
import { TENANT } from '../../config';
import { ssoNav, ssoStartUrl, useSsoConfig } from '../../services/ssoApi';

interface SignInStepProps {
  email: string;
  password: string;
  loading: boolean;
  error?: string | null;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onSubmit: () => void;
  onSwitchToSignUp?: () => void;
  /** Where Microsoft sign-in should return to afterwards: the carried destination (path + plain query), or null. */
  ssoReturnTo?: string | null;
  /** Changes when the server said this account signs in with Microsoft; moves focus to that button. */
  ssoNudge?: number;
}

interface FieldErrors {
  email: string;
  password: string;
}


export function SignInStep({
  email,
  password,
  loading,
  error,
  onEmailChange,
  onPasswordChange,
  onSubmit,
  onSwitchToSignUp,
  ssoReturnTo = null,
  ssoNudge = 0,
}: SignInStepProps) {
  // Null while loading and whenever SSO is off: the screen is then exactly the password form.
  const sso = useSsoConfig();
  const [otherOptionsOpen, setOtherOptionsOpen] = useState(false);
  const microsoftRef = useRef<HTMLDivElement>(null);
  // The password form is the whole screen unless SSO is on; then it sits behind the disclosure
  // (and stays open while it has an error to show).
  const showForm = !sso || otherOptionsOpen || Boolean(error);
  useEffect(() => {
    if (ssoNudge > 0) microsoftRef.current?.querySelector('button')?.focus();
  }, [ssoNudge, sso]);
  const [showPassword, setShowPassword] = useState(false);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [dirtyFields, setDirtyFields] = useState<Set<keyof FieldErrors>>(new Set());
  const firstInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Desktop only: on touch devices, auto-focusing pops the keyboard on load and fights
    // the mobile layout. `pointer: fine` is true for mouse/trackpad, false for touch.
    if (typeof window !== 'undefined' && !window.matchMedia('(pointer: fine)').matches) return;
    const timer = setTimeout(() => {
      firstInputRef.current?.focus();
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  const validateField = (field: keyof FieldErrors, value: string): string => {
    switch (field) {
      case 'email':
        if (!value.trim()) return 'Please enter your email.';
        if (!value.includes('@') || !value.includes('.')) return 'Please enter a valid email address.';
        return '';
      case 'password':
        if (!value) return 'Please enter your password.';
        return '';
      default:
        return '';
    }
  };

  const errors: FieldErrors = {
    email: validateField('email', email),
    password: validateField('password', password),
  };

  const markDirty = (field: keyof FieldErrors) => {
    setDirtyFields(prev => new Set(prev).add(field));
  };

  const shouldShowError = (field: keyof FieldErrors): boolean => {
    if (!errors[field]) return false;
    return attemptedSubmit || dirtyFields.has(field);
  };

  const isValid = () => {
    return !errors.email && !errors.password;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setAttemptedSubmit(true);

    if (isValid()) {
      onSubmit();
    }
  };

  const getInputClassName = (field: keyof FieldErrors) => {
    const baseClass = "w-full px-4 py-4 text-lg rounded-xl border-2 bg-background text-foreground placeholder:text-muted-foreground focus:outline-none transition-colors";

    if (shouldShowError(field)) {
      return `${baseClass} border-destructive focus:border-destructive`;
    }

    return `${baseClass} border-border focus:border-primary`;
  };

  return (
    <div className="flex flex-col flex-1 gap-6 md:block md:flex-none md:space-y-8">
      <div className="space-y-4 shrink-0">
        <h1 className="text-4xl md:text-5xl font-medium">
          Welcome back
        </h1>
        <p className="text-lg text-muted-foreground">
          Sign in to your {TENANT.branding.name} account
        </p>
      </div>

      {sso && (
        <div className="space-y-4 shrink-0">
          <div ref={microsoftRef}>
            <Button
              type="button"
              onClick={() => ssoNav.go(ssoStartUrl(sso.startPath, ssoReturnTo))}
              className="w-full h-14 text-lg gap-3"
            >
              <svg aria-hidden="true" className="w-5 h-5" viewBox="0 0 21 21">
                <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
              </svg>
              Sign in with Microsoft
            </Button>
          </div>
          <button
            type="button"
            aria-expanded={showForm}
            aria-controls="signin-other-options"
            onClick={() => setOtherOptionsOpen((o) => !o)}
            className="w-full text-sm text-muted-foreground underline"
          >
            Other sign-in options (administrator break-glass or a non-Microsoft account)
          </button>
        </div>
      )}

      {showForm && (
      <form id="signin-other-options" onSubmit={handleSubmit} noValidate className="flex flex-col flex-1 gap-6 md:block md:gap-0 md:space-y-6">
        <div className="space-y-4">
          {/* Email */}
          <div>
            <input
              ref={firstInputRef}
              type="email"
              value={email}
              onChange={(e) => { onEmailChange(e.target.value); markDirty('email'); }}
              placeholder="Email address"
              className={getInputClassName('email')}
              autoComplete="email"
            />
            {shouldShowError('email') && (
              <InlineError className="mt-1.5 text-sm">{errors.email}</InlineError>
            )}
          </div>

          {/* Password */}
          <div>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => { onPasswordChange(e.target.value); markDirty('password'); }}
                placeholder="Password"
                className={`${getInputClassName('password')} !pr-20`}
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                // Prevent the tap from stealing focus/blurring the input — otherwise on mobile
                // the keyboard dismisses mid-tap, the layout shifts, and the click is cancelled
                // ("tapping does nothing"). Keeping focus makes the toggle fire reliably.
                onMouseDown={(e) => e.preventDefault()}
                className="absolute right-9 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-11 h-11 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
            {shouldShowError('password') && (
              <InlineError className="mt-1.5 text-sm">{errors.password}</InlineError>
            )}
            <Link
              to="/forgot-password"
              className="text-sm font-semibold text-foreground underline mt-2 inline-block"
            >
              Forgot password?
            </Link>
          </div>
        </div>

        {/* Inline auth error */}
        <InlineError className="text-base">{error}</InlineError>

        {/* Don't have account link */}
        {onSwitchToSignUp && (
          <div className="text-center">
            <span className="text-muted-foreground">Don't have an account? </span>
            <button
              type="button"
              onClick={onSwitchToSignUp}
              className="font-semibold text-foreground underline"
            >
              Create Account
            </button>
          </div>
        )}

        {/* Securities disclaimer — mobile only (desktop uses the page footer). Pushed to just
            above the CTA via mt-auto. */}
        <SecuritiesDisclaimer className="mt-auto md:hidden" />

        {/* The ONLY sticky element — CTA is the bottommost item; it pins to the viewport
            bottom on mobile, inline on desktop. */}
        <div className="sticky bottom-0 z-10 bg-background pt-3 pb-[max(0.5rem,env(safe-area-inset-bottom))] md:static md:z-auto md:bg-transparent md:pt-0 md:pb-0">
          <Button
            type="submit"
            disabled={loading}
            className="w-full h-14 text-lg"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </div>
      </form>
      )}
    </div>
  );
}
